import "./styles/theme.scss";
import "./styles/app.scss";
import React, { useEffect, useRef } from "react";
import { Provider, useSelector } from "react-redux";
import { store, routerHistory, dispatch } from "./redux/redux-config";
import { ConnectedRouter } from "connected-react-router";
import { Route, Switch, Redirect } from "react-router";
import TopBar from "./components/top-bar.jsx";
import SideBar from "./components/side-bar.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import Help from "./pages/Help.jsx";
import SendAlert from "./pages/SendAlert.jsx";
import AlertDetails from "./pages/AlertDetails.jsx";
import MessagingEmbedPage from "./pages/ParishSoft/MessagingEmbedPage.jsx"; // POC route
import MessagesEmbedPage from "./pages/ParishSoft/MessagesEmbedPage.jsx";
import SendAlertEmbed from "./pages/ParishSoft/sendAlertEmbed.jsx";
import MessagingIframePage from "./pages/ParishSoft/MessagingIframePage.jsx";
import { MobileWidth, Path, UserRole } from "./utilities/constants";
import { showToast, ToastContainer } from "./components/toast";
import PeoplePage from "./pages/People";
import AddPersonPage from "./pages/AddPerson";
import ArchivedPeoplePage from "./pages/ArchivedPeople";
import ImportPersonPage from "./pages/import/ImportPerson";
import GroupsPage from "./pages/Groups";
import Templates from "./pages/Templates";
import TemplateCreate from "./pages/TemplateCreate";
import { cleanupPendo } from "./utilities/pendo";
import { usePendoBootstrap, usePendoCleanup, usePendoParishSoftBootstrap } from "./utilities/pendo-hooks";
import GroupMembers from "./pages/GroupMembers";
import useAutoLogout from "./utilities/auto-logout.js";
import actions from "./actions/actions";
import Dashboard from "./pages/Dashboard.jsx";
import { ValidateUnitySession } from "./actions/user-actions.js";
import Messages from "./pages/Messages/Messages.jsx";
import SetupPassword from "./pages/SetupPassword.jsx";
import PeopleDashboard from "./pages/People-Dashboard.jsx";
import AlertDetailsEmbed from "./pages/ParishSoft/AlertDetailsEmbed.jsx";
import ParishSoftSessionExpired from "./pages/ParishSoft/ParishSoftSessionExpired.jsx";
import AccountPage from "./pages/Account.jsx";
import FacebookCallback from "./pages/FacebookCallback.jsx";

const ReportsPage = React.lazy(() => import("./pages/Reports.jsx"));
const PUBLIC_PATHS = [
  "/setup-password",
  "/messaging-embed",
  "/messages-embed",
  "/messaging-iframe",
  "/send-message-embed",
  "/embeded-alert",
  "/parishsoft-session-expired",
];
// ParishSoft embed pages run inside a third-party iframe. Their session is governed by the
// embed's own token/JWT (not user inactivity), so the app-wide idle auto-logout must not apply here —
// otherwise a period of inactivity inside the ParishSoft host page forces a logout and surfaces the
// "session expired" screen even though the embed session is still valid.
const PARISH_SOFT_EMBED_PATHS = [
  "/messaging-embed",
  "/messages-embed",
  "/messaging-iframe",
  "/send-message-embed",
  "/embeded-alert",
];

const LazyReportsPage = () => (
  <React.Suspense
    fallback={
      <div className="loading-page">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    }
  >
    <ReportsPage />
  </React.Suspense>
);

function AdminRoute({ component: Component, path }) {
  const userType = useSelector((store) => store.user?.userType);
  const userLoaded = useSelector((store) => store.user?.loaded);
  return (
    <Route
      path={path}
      render={() => {
        if (!userLoaded) return null;
        return userType === UserRole.Admin
          ? <Component />
          : <Redirect to={Path.Dashboard} />;
      }}
    />
  );
}

export default function App() {
  window.hgsVersion = "2.4.1";
  return (
    <Provider store={store}>
      <ConnectedRouter history={routerHistory}>
        <PageContent />
      </ConnectedRouter>
    </Provider>
  );
}

function PageContent() {
  let isSidebarExpanded = useSelector((store) => store.page.sidebarExpanded);
  let isMobile = useSelector((store) => store.page.isMobile);
  let mobileRef = useRef(isMobile);
  let user = useSelector((store) => store.user);
  let hasLoginToken = useSelector((store) => store.user.token);
  const currentPath = window.location.pathname;
  const redirectParam = new URLSearchParams(window.location.search).get('redirect');
  const isPublicRoute = PUBLIC_PATHS.some((path) =>
    currentPath.startsWith(path)
  );
  let groupsTotalCount = useSelector((store) => store.groups?.totalCount ?? null);
  let peopleTotalCount = useSelector(
    (store) => store.people?.totalCount ?? store.user?.totalMembers ?? store.user?.TotalMembers ?? null
  );
  const isJwtLogin =
    currentPath === "/" && window.location.search.startsWith("?jwt=");
  const isParishSoftEmbedRoute = PARISH_SOFT_EMBED_PATHS.some((path) =>
    currentPath.startsWith(path)
  );
  useAutoLogout(
    actions.user.logout,
    1800000,
    60000,
    user.loaded && !isParishSoftEmbedRoute
  );
  usePendoBootstrap({
    user, hasLoginToken, history: routerHistory, groupsTotalCount,
    peopleTotalCount,
  });
  usePendoParishSoftBootstrap({ history: routerHistory });
  usePendoCleanup(hasLoginToken);

  mobileRef.current = isMobile;
  useEffect(() => {
    function resized() {
      let isMobile = window.innerWidth <= MobileWidth;
      if (isMobile !== mobileRef.current)
        dispatch("IS_MOBILE_CHANGED", isMobile);
    }

    async function refreshAuthToken() {
      try {
        const currentToken = sessionStorage.getItem("unitySessionKey");
        if (!currentToken) return;

        const response = await ValidateUnitySession(currentToken);
        if (!response.success) {
          // logging out the user
          logout();
        }
      } catch (error) {
        showToast("error", error);
        logout();
      }
    }

    if (hasLoginToken) {
      // After login, honour a ?redirect= param written by the service worker when it
      // opened a new window from a push notification click (no existing window was found).
      const redirectTarget = new URLSearchParams(window.location.search).get('redirect');
      if (redirectTarget && redirectTarget.startsWith('/')) {
        routerHistory.replace(redirectTarget);
      }

      const intervalId = setInterval(refreshAuthToken, 600000); // 10 minutes

      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }
    window.addEventListener("resize", resized);
    return () => window.removeEventListener("resize", resized);
  }, [hasLoginToken]);

  useEffect(() => {
    if (!hasLoginToken && !isPublicRoute && !isJwtLogin && !redirectParam) {
      routerHistory.replace("/");
    }
  }, [hasLoginToken, isPublicRoute, isJwtLogin, redirectParam]);

  if (!hasLoginToken) {
    if (isPublicRoute) {
      return (
        <>
          <div className="page-content no-auth">
            <Switch>
              <Route path={Path.SetupPassword} component={SetupPassword} />
              <Route path="/messaging-embed" component={MessagingEmbedPage} />
              <Route path="/messages-embed" component={MessagesEmbedPage} />
              <Route path="/messaging-iframe" component={MessagingIframePage} />
              <Route path="/send-message-embed" component={SendAlertEmbed} />
              <Route path="/embeded-alert" component={AlertDetailsEmbed} />
              <Route path="/parishsoft-session-expired" component={ParishSoftSessionExpired} />
            </Switch>
          </div>
          <ToastContainer />
        </>
      );
    }

    if (
      window.location.pathname === "/" &&
      window.location.search.startsWith("?jwt=")
    ) {
      return <LoginPage />;
    } else {
      // Preserve ?redirect= so the user is forwarded to the right page after login
      // (written by the service worker when opening a new window for a push notification).
      const redirectParam = new URLSearchParams(window.location.search).get('redirect');
      // Preserve /reset?ID=... so the password-reset-via-email-link flow can read the token from the URL.
      if (!redirectParam && currentPath !== "/reset") routerHistory.replace("/");
      return <LoginPage />;
    }
  }

  function logout() {
    sessionStorage.removeItem("session");
    sessionStorage.removeItem("unitySessionKey");
    localStorage.removeItem("session");
    localStorage.removeItem("unitySessionKey");
    // Remove AiDE and Pendo UI when user logs out
    cleanupPendo();
    dispatch("LOGOUT");
  }
  // Authenticated special-case bare embed route (no top bar / side bar)
  const pathName = window.location.pathname;
  const isBareEmbed =
    pathName.startsWith('/messages-embed') ||
    pathName.startsWith('/send-message-embed') ||
    pathName.startsWith('/embeded-alert') ||
    pathName.startsWith('/parishsoft-session-expired');

  if (isBareEmbed) {
    return (
      <>
        <div className="page-content no-chrome">
          <ConnectedRouter history={routerHistory}>
            <Switch>
              <Route path="/messages-embed" component={MessagesEmbedPage} />
              <Route path="/send-message-embed" component={SendAlertEmbed} />
              <Route path="/embeded-alert" component={AlertDetailsEmbed} />
              <Route path="/parishsoft-session-expired" component={ParishSoftSessionExpired} />
            </Switch>
          </ConnectedRouter>
        </div>
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <SideBar />
      <div
        className={
          "page-content" +
          (isSidebarExpanded ? " expanded-side-bar" : "") +
          (isMobile ? " mobile" : "")
        }
      >
        <ConnectedRouter history={routerHistory}>
          <Switch>
            <Route path={Path.Send} component={SendAlert} />
            <Route path={Path.CreateTemplate} component={TemplateCreate} />
            <Route path={Path.EditTemplate} component={TemplateCreate} />
            <Route path={Path.Templates} component={Templates} />
            {/* <Route path={Path.Sent} component={Successful} /> */}
            {/* <Route path={Path.Track} component={MyAlerts} /> */}
            <Route path={Path.AlertDetails} component={AlertDetails} />
            <Route path={Path.AddPerson} component={AddPersonPage} />
            <Route path={Path.ImportPerson} component={ImportPersonPage} />
            <Route path={Path.ArchivedPeople} component={ArchivedPeoplePage} />
            <Route path={Path.People} component={PeoplePage} />
            <Route path={Path.Groups} component={GroupsPage} />
            <Route path={Path.PeopleGroups} component={GroupsPage} />
            <Route path={Path.GroupMembers} component={GroupMembers} />
            <Route path={Path.ReportCommunication} component={LazyReportsPage} />
            <Route path={Path.ReportRecent} component={LazyReportsPage} />
            <Route path={Path.ReportInvalidContacts} component={LazyReportsPage} />
            <Route path={Path.ReportOptOut} component={LazyReportsPage} />
            <Route path={Path.Reports} component={LazyReportsPage} />
            <AdminRoute path={Path.Account} component={AccountPage} />
            {/* <Route path={Path.Settings} component={LazySettingsPage} /> */}
            <Route path={Path.Help} component={Help} />
            <Route path={Path.Messages} component={Messages} />
            <Route path={Path.PeopleMessages} component={Messages} />
            {/* Facebook OAuth callback route */}
            <Route path="/facebook/callback" component={FacebookCallback} />
            {/* Embed POC routes */}
            <Route path="/messaging-embed" component={MessagesEmbedPage} />
            <Route path="/messages-embed" component={MessagesEmbedPage} />
            <Route path="/messaging-iframe" component={MessagingIframePage} />
            <Route path="/send-message-embed" component={SendAlertEmbed} />
            <Route path="/embeded-alert" component={AlertDetailsEmbed} />
            <Route path="/parishsoft-session-expired" component={ParishSoftSessionExpired} />
            {/*End Embed POC routes */}

            <Route exact path={Path.Dashboard} component={Dashboard} />
            <Route exact path={Path.PeopleDashboard} component={PeopleDashboard} />

            <Route path={Path.SetupPassword} component={SetupPassword} />
            {/* use exact for path matching as dashboard url is / generic url or keep dashboard at the bottom of all the routes*/}
          </Switch>
        </ConnectedRouter>
      </div>
      <ToastContainer />
    </>
  );
}
